import { getCurrentOrgID } from './organization';
import ErrorHandler from '../utils/ErrorHandler';
import { supabase, queryClient } from '../data/client';
import { QueryKeys } from '@/utils/react-query/QueryKeys';

const getShortcuts = async (personID) => {
    const orgID = await getCurrentOrgID();
    
    const { data, error } = await supabase
        .from("Shortcuts")
        .select("*")
        .eq("organizationID", orgID)
        .eq("personID", personID)
        .eq("isRecent", false)
        .order("position", { ascending: true });

    if (error) {
        ErrorHandler(Error(error.message), { query: "getShortcuts", personID, error }, "Something went wrong fetching shortcuts");
        throw Error();
    }

    return data;
};

const getRecentShortcuts = async (personID) => {
    const orgID = await getCurrentOrgID();
    
    const { data, error } = await supabase
        .from("Shortcuts")
        .select("*")
        .eq("organizationID", orgID)
        .eq("personID", personID)
        .eq("isRecent", true)
        .order("updatedAt", { ascending: false })
        .limit(5);

    if (error) {
        ErrorHandler(Error(error.message), { query: "getRecentShortcuts", personID, error }, "Something went wrong fetching recent shortcuts");
        throw Error();
    }

    return data;
};

const createShortcut = async ({ personID, screen, parameters = null, label = null, icon = null, library = null }) => {
    const orgID = await getCurrentOrgID();
    
    const { data: existingShortcuts } = await supabase
        .from("Shortcuts")
        .select("position")
        .eq("organizationID", orgID)
        .eq("personID", personID)
        .eq("isRecent", false)
        .order("position", { ascending: false })
        .limit(1);

    const nextPosition = existingShortcuts?.length > 0 ? (existingShortcuts[0].position || 0) + 1 : 0;

    const { data, error } = await supabase
        .from("Shortcuts")
        .insert({
            organizationID: orgID,
            personID,
            screen,
            parameters: { ...parameters, label, icon, library },
            position: nextPosition,
            isRecent: false
        })
        .select()
        .single();

    if (error) {
        ErrorHandler(Error(error.message), { mutation: "createShortcut", input: { personID, screen, parameters, label, icon, library }, error }, "Something went wrong creating shortcut");
        throw Error();
    }

    return data;
};

const updateShortcut = async ({ id, updates }) => {
    const { data, error } = await supabase
        .from("Shortcuts")
        .update({ ...updates, updatedAt: new Date().toISOString() })
        .eq("id", id)
        .select()
        .single();

    if (error) {
        ErrorHandler(Error(error.message), { mutation: "updateShortcut", input: { id, updates }, error }, "Something went wrong updating shortcut");
        throw Error();
    }

    return data;
};

const deleteShortcut = async (id) => {
    const { error } = await supabase
        .from("Shortcuts")
        .delete()
        .eq("id", id);

    if (error) {
        ErrorHandler(Error(error.message), { mutation: "deleteShortcut", input: id, error }, "Something went wrong deleting shortcut");
        throw Error();
    }

    return true;
};

const reorderShortcuts = async (personID, orderedIds) => {
    const orgID = await getCurrentOrgID();
    
    const updates = orderedIds.map((id, index) => ({
        id,
        position: index,
        organizationID: orgID,
        personID
    }));

    for (const update of updates) {
        const { error } = await supabase
            .from("Shortcuts")
            .update({ position: update.position })
            .eq("id", update.id);

        if (error) {
            ErrorHandler(Error(error.message), { mutation: "reorderShortcuts", input: { personID, orderedIds }, error }, "Something went wrong reordering shortcuts");
            throw Error();
        }
    }

    return true;
};

const trackRecentScreen = async ({ personID, screen, parameters = null, label = null, icon = null }) => {
    const orgID = await getCurrentOrgID();
    
    const { data: existing } = await supabase
        .from("Shortcuts")
        .select("id")
        .eq("organizationID", orgID)
        .eq("personID", personID)
        .eq("screen", screen)
        .eq("isRecent", true)
        .maybeSingle();

    if (existing) {
        await supabase
            .from("Shortcuts")
            .update({ updatedAt: new Date().toISOString(), parameters: { ...parameters, label, icon } })
            .eq("id", existing.id);
    } else {
        await supabase
            .from("Shortcuts")
            .insert({
                organizationID: orgID,
                personID,
                screen,
                parameters: { ...parameters, label, icon },
                isRecent: true
            });

        const { data: recentShortcuts } = await supabase
            .from("Shortcuts")
            .select("id")
            .eq("organizationID", orgID)
            .eq("personID", personID)
            .eq("isRecent", true)
            .order("updatedAt", { ascending: false });

        if (recentShortcuts?.length > 10) {
            const idsToDelete = recentShortcuts.slice(10).map(s => s.id);
            await supabase
                .from("Shortcuts")
                .delete()
                .in("id", idsToDelete);
        }
    }

    queryClient.invalidateQueries({ queryKey: QueryKeys.shortcuts.recentByPersonId(personID) });
};

export {
    getShortcuts,
    getRecentShortcuts,
    createShortcut,
    updateShortcut,
    deleteShortcut,
    reorderShortcuts,
    trackRecentScreen
};
